/**
 * Generic object pool for reusing expensive objects.
 * Prevents GC spikes from frequent create/destroy cycles.
 */
export class ObjectPool<T> {
  private pool: T[] = [];
  private factory: () => T;
  private reset: (obj: T) => void;
  private maxSize: number;

  constructor(
    factory: () => T,
    reset: (obj: T) => void,
    initialSize: number = 0,
    maxSize: number = 1000
  ) {
    this.factory = factory;
    this.reset = reset;
    this.maxSize = maxSize;

    // Pre-populate pool
    for (let i = 0; i < initialSize; i++) {
      this.pool.push(factory());
    }
  }

  /**
   * Get an object from the pool, or create a new one if empty.
   */
  acquire(): T {
    return this.pool.pop() ?? this.factory();
  }

  /**
   * Return an object to the pool after resetting it.
   */
  release(obj: T): void {
    if (this.pool.length < this.maxSize) {
      this.reset(obj);
      this.pool.push(obj);
    }
    // If pool is full, object is discarded (will be GC'd)
  }

  /**
   * Get the current pool size (available objects).
   */
  get size(): number {
    return this.pool.length;
  }

  /**
   * Clear all objects from the pool.
   */
  clear(): void {
    this.pool = [];
  }

  /**
   * Pre-warm the pool to a specific size.
   */
  prewarm(count: number): void {
    while (this.pool.length < count && this.pool.length < this.maxSize) {
      this.pool.push(this.factory());
    }
  }
}
